<template>
  <div class="weekly-tales-container mb-2 mobile-weekly">
    <v-dialog v-model="showDeleteDialog" persistent max-width="450">
      <v-card class="mt-0 mb-0">
        <v-card-title class="subtitle-1">
          {{ lang.general.areYouSure }}
        </v-card-title>

        <v-card-text>
          <p class="subtitle-2">
            {{ lang.general.byDelete }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            elevation="0"
            small
            class="primary--text"
            @click="closeDeleteDialog()"
          >
            {{ lang.actions.cancel }}
          </v-btn>
          <v-btn small class="ml-2 btn-bg" elevation="0" @click="deleteStory()">
            {{ lang.actions.delete }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition mt-0 mb-0"
    >
      <v-card class="mt-0 mb-0">
        <v-row id="weekly-tales" class="no-gutters pt-0" v-if="currentStory">
          <div class="progress-bar-container">
            <div
              class="progress-bar"
              :style="{ width: progressWidth + '%' }"
            ></div>
          </div>
          <v-col class="col-12 image-container">
            <v-icon @click="closeDialog()" class="close-dialog"
              >mdi-close</v-icon
            >
            <span
              v-if="currentStory.deceasedFullName"
              class="full-name"
              @click="visitOnlyProfile(currentStory.deceasedId)"
              >{{ currentStory.deceasedFullName }}</span
            >
            <v-icon
              @click="leftStory()"
              class="back-image"
              v-if="storyShowIndex != 0"
              >mdi-chevron-left</v-icon
            >
            <v-icon
              v-if="storyShowIndex != weeklyTales.length - 1"
              @click="rightStory()"
              class="next-image"
              >mdi-chevron-right</v-icon
            >
            <div>
              <img
                :src="currentStory.url"
                alt="Story Image"
                :lazy-src="require('../assets/images/lazy-loading.png')"
              />
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
    <v-file-input
      ref="fileInput"
      style="display: none"
      @change="onStoryUpload"
      accept="image/*"
      v-model="newStory"
      hide-input
      show-size
      prepend-icon="mdi-camera"
      truncate-length="27"
    ></v-file-input>
    <v-tooltip right>
      <template v-slot:activator="{ on, attrs }">
        <div
          :class="{ 'd-none': !showAdd }"
          class="weekly-tales-container-item adding"
          @click="triggerStoryUpload()"
          v-bind="attrs"
          v-on="on"
        >
          <v-btn fab x-large elevation="0">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
      <span v-html="lang.profile.weeklyTales.weeklyTalesAddTooltip"> </span>
    </v-tooltip>
    <div
      v-for="(story, index) in weeklyTales"
      :key="story.id"
      class="weekly-tales-container-item"
      :class="{ visited: visited }"
      @click="showStory(index)"
    >
      <div class="weekly-tales-container-inner-item-outer">
        <v-icon
          v-if="showAdd && !demoMode"
          @click="deleteStoryDialog(story.storyId, $event)"
          class="trash-icon"
          >mdi-trash-can</v-icon
        >
        <div
          class="weekly-tales-container-inner-item"
          :style="{ backgroundImage: 'url(' + story.url + ')' }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Compressor from "compressorjs";

export default {
  name: "WeeklyTales",
  props: {
    showAdd: {
      type: Boolean,
      default: true,
    },
    weeklyTales: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      deleteStoryId: null,
      showDeleteDialog: false,
      newStory: null,
      storyShowIndex: null,
      dialog: false,
      visited: false,
      progressWidth: 0,
      duration: 5000,
      intervalId: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentUser"]),
    lang() {
      return this.$store.state.langObj;
    },
    demoMode() {
      return this.$route.path.includes("demo");
    },
    currentStory() {
      if (this.storyShowIndex != null) {
        return this.weeklyTales[this.storyShowIndex];
      }
      return null;
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleArrowPress);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleArrowPress);
  },
  methods: {
    deleteStory() {
      this.$store
        .dispatch("weeklyTales/deleteDeceasedStory", {
          deceasedId: this.$route.params.id,
          storyId: this.deleteStoryId,
        })
        .then(() => {
          this.$emit("getDeceasedStories");
          this.closeDeleteDialog();
        });
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
      this.deleteStoryId = null;
    },
    deleteStoryDialog(id, event) {
      // Prevent event bubbling to parent
      event.stopPropagation();
      this.deleteStoryId = id;
      this.showDeleteDialog = true;
    },
    setBrowserTheme(color = false) {
      if (!color) {
        color = "#3093a5";
        switch (
          this.currentUser?.theme ||
          window.localStorage.getItem("theme") ||
          "no-theme"
        ) {
          case "no-theme":
          case "theme-default":
            color = "#3093a5";
            break;
          case "theme-black":
            color = "#000000";
            break;
          case "theme-blue-gray":
            color = "#607D8B";
            break;
          case "theme-blue":
            color = "#1E88E5";
            break;
          case "theme-cyan":
            color = "#00B8D4";
            break;
          case "theme-dark-red":
            color = "#D50000";
            break;
          case "theme-green":
            color = "#64DD17";
            break;
          case "theme-light-green":
            color = "#00C853";
            break;
          case "theme-light-brown":
            color = "#8D6E63";
            break;
          case "theme-lime-dark":
            color = "#9E9D24";
            break;
          case "theme-orange":
            color = "#F57C00";
            break;
          case "theme-pink":
            color = "#FF4081";
            break;
          case "theme-light-purple":
            color = "#E040FB";
            break;
          case "theme-purple":
            color = "#800080";
            break;
        }

        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute("content", color);
      } else {
        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute("content", color);
      }
    },
    startProgress() {
      this.clearProgress(); // Clear any previous intervals
      this.progressWidth = 0;
      const step = 100 / (this.duration / 10); // Calculate step size for each interval (10 ms)

      this.intervalId = setInterval(() => {
        this.progressWidth += step;
        if (this.progressWidth >= 100) {
          clearInterval(this.intervalId);
          // Automatically proceed to the next story or handle story end here
          this.rightStory();
        }
      }, 10);
    },
    clearProgress() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
    visitOnlyProfile(id) {
      if (this.$route.path.includes("demo")) {
        this.$emit("demoMode");
        return;
      }
      this.$router.push({ name: "Profile", params: { id: id } });
    },
    onStoryUpload(file) {
      this.$store.commit("loading", true);
      if (file) {
        var notValid = file.size / (1024 * 1024) >= 10; // 10mb allowed per image
        if (notValid) {
          this.$store.commit("toast", {
            message: this.lang.general.maximumImageSize,
            color: "red lighten-2",
          });
          this.$store.commit("loading", false);
          return;
        }

        new Compressor(file, {
          quality: 0.8,
          success: (compressedFile) => {
            var fd = new FormData();
            fd.append("deceasedId", this.$route.params.id); // Append additional data
            fd.append("file", compressedFile, file.name);
            fd.append("typeType", 1); // Example: 1 for Deceased, 2 for User

            this.$store
              .dispatch("uploadDeceasedStory", {
                fd: fd,
                deceasedId: this.$route.params.id,
              })
              .then((response) => {
                this.newStory = null;
                this.$store.commit("loading", false);
                this.$emit("getDeceasedStories");
              });
          },
          error(err) {
            this.$store.commit("loading", false);
          },
        });
      }
    },
    triggerStoryUpload() {
      if (this.$route.path.includes("demo")) {
        this.$emit("demoMode");
        return;
      }
      this.$refs.fileInput.$el.querySelector("input").click();
    },
    showStory(index) {
      if (this.weeklyTales.length) {
        this.storyShowIndex = index;
        this.dialog = true;
        this.startProgress();
        this.setBrowserTheme("#1c1e21");
      }
    },
    closeDialog() {
      this.dialog = false;
      this.storyShowIndex = null;
      this.clearProgress();
      this.setBrowserTheme();
    },
    leftStory() {
      if (this.storyShowIndex != 0) {
        this.storyShowIndex--;
        this.startProgress();
      }
    },
    rightStory() {
      if (this.storyShowIndex != this.weeklyTales.length - 1) {
        this.storyShowIndex++;
        this.startProgress();
      } else {
        this.closeDialog();
      }
    },
    handleArrowPress(event) {
      if (event.key === "ArrowRight") {
        this.rightStory();
      } else if (event.key === "ArrowLeft") {
        this.leftStory();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.trash-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  color: #ffffff;
  padding: 4px;
  cursor: pointer;
  z-index: 1;
  background-color: rgba(220, 53, 69, 0.9);
  border-radius: 50%;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(220, 53, 69, 1);
    transform: scale(1.1);
  }
}
#weekly-tales {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
}
.progress-bar-container {
  width: 100%;
  height: 3px;
  background-color: lightgray; /* Background color */
  overflow: hidden;
  position: relative;
}
.progress-bar {
  height: 100%;
  background-color: dodgerblue; /* Progress color */
  transition: width 0.01s linear;
}
.full-name {
  text-decoration: underline;
  color: white;
  font-size: 20px;
  position: absolute;
  top: 0;
  left: 40px;
  padding: 10px;
  margin-left: 20px;
  cursor: pointer;
  text-shadow: 0px 0px 3px rgba(0, 0, 0, 0.5);
}
.image-container {
  position: relative;
  background: #1c1e21;
  min-height: calc(100vh - 3px);
  display: flex;
  justify-content: center;
  align-items: center;
  .v-image,
  img,
  video {
    max-width: 100vw;
    max-height: calc(100vh - 10px);
    margin: auto auto;
    // max-width: 100%;
    // max-height: 100vh;
  }
  .close-dialog {
    position: absolute;
    top: 0;
    left: 0;
    color: #fff;
    padding: 15px;
    text-shadow: 0px 0px 3px rgba(0, 0, 0, 0.5);
    &:hover {
      background: rgb(27 27 27 / 80%);
    }
  }
  .next-image {
    position: absolute;
    position: absolute;
    top: 49%;
    right: 8px;
    z-index: 5;
    color: #fff;
    padding: 15px 15px;
    text-shadow: 0px 0px 3px rgba(0, 0, 0, 0.5);
    &:hover {
      background: rgb(27 27 27 / 80%);
    }
    &::after {
      background: transparent !important;
    }
  }
  .back-image {
    position: absolute;
    position: absolute;
    top: 49%;
    left: 0;
    z-index: 5;
    color: #fff;
    padding: 15px 15px;
    text-shadow: 0px 0px 3px rgba(0, 0, 0, 0.5);
    &:hover {
      background: rgb(27 27 27 / 80%);
    }
    &::after {
      background: transparent !important;
    }
  }
}
.weekly-tales-container {
  display: flex;
  overflow: auto;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: flex-start;
  gap: 10px;
  padding-bottom: 5px;
  &::-webkit-scrollbar {
    height: 7px !important;
    background: transparent !important;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent !important;
  }
  .weekly-tales-container-item {
    width: 85px;
    flex: 0 0 85px;
    height: 85px;
    background: rgb(147, 243, 193);
    background: linear-gradient(
      124deg,
      rgba(147, 243, 193, 1) 10%,
      rgba(190, 239, 116, 1) 54%,
      rgba(243, 183, 20, 1) 84%,
      rgba(16, 241, 225, 1) 100%
    );
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.5s ease;
    position: relative;
    &.visited {
      background: rgb(245, 245, 245);
    }
    &.adding {
      background: rgb(254, 254, 254);
    }

    .weekly-tales-container-inner-item-outer {
      width: 77px;
      height: 77px;
      border-radius: 50%;
      background: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .weekly-tales-container-inner-item {
      width: 70px;
      height: 70px;
      border-radius: 50%;
      background-size: cover;
      background-position: center;
    }
  }
}
</style>